/* global TokenizeJs */
/* eslint-disable require-atomic-updates */
import { isEqual } from 'lodash';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { AppDispatcher } from '@wsb/guac-widget-shared/lib/common/ols-core/shared-bundle';
import { UX2 } from '@wsb/guac-widget-core';
import { CartApi } from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { PoyntPaymentRequestActions } from '../utils/ols-core/';
import * as PaymentRequestUtils from '../utils/ols-core/PaymentRequestApi';
import { getOrderCompleteUrl } from '../utils/ols-core/getOrderCompleteUrl';
import * as PoyntEvents from '../../constants/poyntPaymentEvents';
import * as Metrics from './utils/trackMetric';
import * as MetricEvents from '../../constants/metrics';
import {
  DigitalWalletConstants,
  APPLE_PAY,
  GOOGLE_PAY
} from '../utils/ols-core/DigitalWalletStore';

const { dispatchViewAction } = AppDispatcher;

const paymentMethodTypes = {
  [APPLE_PAY]: '_apple',
  [GOOGLE_PAY]: '_google'
};

export function PoyntPaymentRequestButton(props) {
  const [order, setOrder] = useState();
  const [gap, setGap] = useState('0');
  const collectRef = useRef();
  const {
    getOrderData,
    phoneRequired,
    theme,
    disabled,
    dataAid,
    initPage,
    setHasAttemptedPurchase,
    experimentEnabled,
    productId,
    isQuickView,
    isLowInventoryInGalleryEnabled,
    isLowInventoryInGallery,
    isGalleryDetail,
    shouldStickToBottom
  } = props;
  const poyntPaymentMethodsContainer = 'poynt-payment-methods-container';
  const isCart = initPage === 'cart';
  const color = theme === 'dark' ? 'black' : 'white';
  const { Block } = UX2.Element;
  let currentVariant,
    currentOrder = order;

  const containers = {
    [APPLE_PAY]: `poynt-applepay-container-${productId}`,
    [GOOGLE_PAY]: `poynt-googlepay-container-${productId}`
  };

  const buttonContainerOptions = {
    width: '100%',
    display: 'flex'
  };

  const buttonOptions = {
    color,
    type: 'buy',
    width: '100%',
    height: '56px',
    margin: '0',
    ...(color === 'white' && { border: '1px solid #000' })
  };

  let shippingAddress;

  const disabledStyle = disabled
    ? {
      pointerEvents: 'none',
      cursor: 'not-allowed',
      opacity: 0.4
    }
    : {};

  useEffect(() => {
    const { order: anOrder } = getOrderData();

    setTimeout(() => {
      setOrder(anOrder || {});
    }, 0);

    return function unmount() {
      const paymentMethods = PoyntPaymentRequestActions.getPaymentMethods();
      if (collectRef.current)
        for (const pm of paymentMethods) {
          collectRef.current.unmount(containers[pm], document);
        }
    };
  }, []);

  useLayoutEffect(() => {
    if (!order) return;

    mountPoyntPaymentButton();
    setupPaymentEvents();
  }, [order]);

  function mountPoyntPaymentButton() {
    const { business_id, application_id, channel_id, store_id } =
      OlsConfigStore.getState().poynt_config;
    const paymentRequest = PoyntPaymentRequestActions.buildPaymentRequest(initPage);
    const collect = new TokenizeJs(
      { business_id, application_id, channel_id, store_id },
      paymentRequest
    );
    collectRef.current = collect;

    function startSession(source, request) {
      switch (source) {
        case APPLE_PAY:
          collect.startApplePaySession(request);
          break;
        case GOOGLE_PAY:
          collect.startGooglePaySession(request);
          break;
        default:
          break;
      }
    }

    async function onClick({ source }) {
      if (!isCart && !getOrderData()?.variant.canCheckout) {
        setHasAttemptedPurchase();
        return;
      }
      const prefix = source === APPLE_PAY ? 'AP_' : 'GP_';

      trackEvent(source, 'BUTTON_CLICK');

      if (isQuickView && isLowInventoryInGalleryEnabled && isLowInventoryInGallery) {
        Metrics.track(MetricEvents[`QUICK_VIEW_MODAL_LOW_IN_STOCK_POYNT_${prefix}BUTTON_CLICK`]);
      } else if (isQuickView && isLowInventoryInGalleryEnabled) {
        Metrics.track(MetricEvents[`QUICK_VIEW_MODAL_POYNT_${prefix}BUTTON_CLICK`]);
      } else if (!isCart && experimentEnabled) {
        const eventName = `POYNT_${prefix}BUTTON_LOW_INVENTORY_CLICK`;
        Metrics.track(MetricEvents[eventName]);
      }

      dispatchViewAction({
        type: DigitalWalletConstants.SESSION_STARTED
      });
      startSession(source, PoyntPaymentRequestActions.updatePaymentRequest(currentOrder));
    }

    collect.supportWalletPayments().then(() => {
      const paymentMethods = PoyntPaymentRequestActions.getPaymentMethods();

      if (paymentMethods.length > 1) {
        buttonOptions.type = 'plain';
        setGap(shouldStickToBottom ? '10px' : '16px');
      }

      if (initPage === 'cart') {
        buttonOptions.type = 'plain';
      }
      for (const pm of paymentMethods) {
        const prefix = pm === APPLE_PAY ? 'AP_' : 'GP_';

        trackEvent(pm, 'BUTTON_IMPRESSION');

        if (isQuickView && isLowInventoryInGalleryEnabled && isLowInventoryInGallery) {
          Metrics.track(
            MetricEvents[`QUICK_VIEW_MODAL_LOW_IN_STOCK_POYNT_${prefix}BUTTON_IMPRESSION`]
          );
        } else if (isQuickView && isLowInventoryInGalleryEnabled) {
          Metrics.track(MetricEvents[`QUICK_VIEW_MODAL_POYNT_${prefix}BUTTON_IMPRESSION`]);
        } else if (!isCart && experimentEnabled) {
          const eventName = `POYNT_${prefix}BUTTON_LOW_INVENTORY_IMPRESSION`;
          Metrics.track(MetricEvents[eventName]);
        }
        collect.mount(containers[pm], document, {
          buttonContainerOptions: {
            style: buttonContainerOptions
          },
          paymentMethods: [pm],
          buttonOptions: {
            ...buttonOptions,
            onClick
          }
        });
      }
    });
  }

  function shippingIsAvailable(shippingRates) {
    return shippingRates?.length;
  }

  function getModifiedLineItems({ id, quantity, options }) {
    const lineItemToUpdate = currentOrder.line_items.find(({ variant_id }) => variant_id === id);
    return [{ id: lineItemToUpdate.id, quantity, options }];
  }

  async function updateOrder() {
    const { variant, order: anOrder } = getOrderData();

    if (isCart && shippingAddress) {
      return PaymentRequestUtils.updateCartShipAddress(anOrder, shippingAddress);
    }

    if (!variant || !variant.values) {
      return currentOrder;
    }

    const {
      values: { id, quantity, options }
    } = variant;

    const orderExists = currentOrder?.total_quantity;
    const quantityChanged = currentVariant?.values.quantity !== quantity;
    const newVariantSelected =
      currentVariant?.values.id !== id ||
      !isEqual(
        currentVariant?.values.options.personalizations_attributes,
        options.personalizations_attributes
      );

    if (!orderExists) {
      currentOrder = await CartApi.createCart({
        order: { require_phone_number: phoneRequired }
      });
      currentOrder = CartApi.addToOrder(currentOrder, id, quantity, options);
    } else if (newVariantSelected) {
      await Promise.all([
        CartApi.removeCartLineItem(currentOrder, currentOrder.line_items[0].id),
        CartApi.addToCart(currentOrder, id, quantity, options)
      ]);
      shippingAddress = {};
    } else if (quantityChanged) {
      const modifiedLineItems = getModifiedLineItems(variant.values);
      await CartApi.modifyCartLineItems(currentOrder, modifiedLineItems);
      shippingAddress = {};
    }

    currentVariant = variant;
    return currentOrder;
  }

  function trackEvent(paymentMethod, eventType) {
    const view = isGalleryDetail ? 'PRODUCT_LIST_POYNT_' : 'PDP_POYNT_';
    const location = isCart ? 'CART_POYNT_' : view;
    const source = paymentMethod === APPLE_PAY ? 'AP_' : 'GP_';
    const eventId = `${location}${source}${eventType}`;

    if (!experimentEnabled) {
      Metrics.track(MetricEvents[eventId]);
    }
  }

  function setupPaymentEvents() {
    const collect = collectRef.current;

    collect.on(PoyntEvents.CLOSE_WALLET, () => {
      dispatchViewAction({
        type: DigitalWalletConstants.SESSION_CANCELED
      });
    });

    collect.on(PoyntEvents.SHIPPING_ADDRESS_CHANGE, async e => {
      try {
        currentOrder = await updateOrder();

        if (
          e.shippingAddress.postalCode !== shippingAddress?.postalCode ||
          !shippingIsAvailable(currentOrder.shipping_rates)
        ) {
          shippingAddress = {
            city: e.shippingAddress.locality,
            region: e.shippingAddress.administrativeArea,
            postalCode: e.shippingAddress.postalCode,
            country: e.shippingAddress.countryCode
          };

          currentOrder = await PaymentRequestUtils.updateCartShipAddress(
            currentOrder,
            shippingAddress
          );
        }

        e.updateWith(PoyntPaymentRequestActions.updatePaymentRequest(currentOrder));
      } catch (error) {
        delete currentOrder.shipping_rates;

        const walletError = {
          code: PoyntEvents.INVALID_SHIPPING_ADDRESS,
          message: 'Unable to update shipping address'
        };

        e.updateWith({ error: walletError });
      }
    });

    collect.on(PoyntEvents.SHIPPING_METHOD_CHANGE, async e => {
      try {
        currentOrder = await PaymentRequestUtils.updateCartShipMethod(currentOrder, {
          id: e.shippingMethod.id
        });
        e.updateWith(PoyntPaymentRequestActions.updatePaymentRequest(currentOrder));
      } catch (error) {
        const walletError = {
          code: PoyntEvents.INVALID_SHIPPING_ADDRESS,
          message: 'Unable to update shipping method'
        };

        e.updateWith({ error: walletError });
      }
    });

    collect.on(PoyntEvents.COUPON_CODE_CHANGE, async e => {
      let couponError = {};

      try {
        currentOrder = await PaymentRequestUtils.applyCoupon(currentOrder, e.couponCode);
      } catch (error) {
        if (error.response?.id) {
          currentOrder = error.response;
          if (e.couponCode) {
            couponError = {
              code: 'invalid_coupon_code',
              message: 'Invalid coupon. Please try again.'
            };
          }
        }
      }

      e.updateWith(PoyntPaymentRequestActions.updatePaymentRequest(currentOrder, couponError));
    });

    collect.on(PoyntEvents.PAYMENT_AUTHORIZED, async e => {
      collect.on(PoyntEvents.ERROR, () => {
        const walletError = {
          code: PoyntEvents.INVALID_PAYMENT_DATA,
          message: 'Unable to process payment'
        };

        e.complete({ error: walletError });
      });

      currentOrder = {
        ...currentOrder,
        shippingAddress: e.shippingAddress,
        billingAddress: e.billingAddress
      };

      dispatchViewAction({
        type: DigitalWalletConstants.SESSION_STARTED
      });

      if (!shippingIsAvailable(currentOrder.shipping_rates)) {
        e.updateWith({
          error: {
            code: PoyntEvents.UNSERVICEABLE_ADDRESS,
            contactField: 'country',
            message: 'No shipping methods available for selected address'
          }
        });
        return;
      }

      try {
        const { processor } = OlsConfigStore.getState().poynt_config;
        const paymentMethod = processor
          ? `${processor}${paymentMethodTypes[e.source]}`
          : `poynt${paymentMethodTypes[e.source]}`;
        const completedOrder = await PaymentRequestUtils.completePoyntOrder(
          currentOrder,
          e.nonce,
          initPage,
          paymentMethod
        );

        e.complete();

        setTimeout(() => {
          trackEvent(e.source, 'COMPLETED_IMPRESSION');
          window.location.href = getOrderCompleteUrl(completedOrder);
        }, 0);
      } catch (error) {
        const walletError = {
          code: PoyntEvents.INVALID_PAYMENT_DATA,
          message: 'Unable to process payment'
        };

        e.complete({ error: walletError });
      }
    });
  }

  return (
    <Block
      id={ poyntPaymentMethodsContainer }
      data-aid={ dataAid }
      style={{
        'display': 'flex',
        'width': '100%',
        gap,
        '@sm': {
          flexDirection: 'row'
        },
        '@xs-only': {
          flexDirection: 'column'
        }
      }}
    >
      <div
        id={ containers[APPLE_PAY] }
        style={{
          ...disabledStyle
        }}
      ></div>
      <div
        id={ containers[GOOGLE_PAY] }
        style={{
          ...disabledStyle
        }}
      ></div>
    </Block>
  );
}

PoyntPaymentRequestButton.propTypes = {
  phoneRequired: PropTypes.bool,
  getOrderData: PropTypes.func,
  theme: PropTypes.string,
  matchingButtonId: PropTypes.string,
  disabled: PropTypes.bool,
  dataAid: PropTypes.string,
  initPage: PropTypes.string.isRequired,
  setHasAttemptedPurchase: PropTypes.func,
  experimentEnabled: PropTypes.bool,
  productId: PropTypes.string,
  isQuickView: PropTypes.bool,
  isLowInventoryInGalleryEnabled: PropTypes.bool,
  isLowInventoryInGallery: PropTypes.bool,
  isGalleryDetail: PropTypes.bool,
  shouldStickToBottom: PropTypes.bool
};
